import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GroupPerson, GroupRole } from '../domain/group-access';
import type { InviteDraft, PendingInvite } from '../domain/invites';
import {
  acceptInvite,
  createStrongerMemoryGroup,
  deleteGroup,
  findVisibleSubGroup,
  inviteToGroup,
  loadGroupPeople,
  loadGroupRoles,
  loadMyInvite,
  loadMyPendingInvites,
  loadPendingInvites,
  loadStrongerMemoryGroups,
  removeGroupMember,
  renameGroup,
  resendInvite,
  type StrongerMemoryGroup,
  setGroupRole,
  withdrawInvite,
} from '../domain/sub-groups';
import { queryKeys } from '../query/keys';
import { removeReadyInvite } from './invite-drafts-store';
import { useConfig, useSession } from './provider';
import { useMe } from './use-me';

/**
 * Account › My groups, from Core: the groups the person belongs to under the Stronger Memory
 * group (`domain/sub-groups.ts`). Off where the environment has no Stronger Memory group, and
 * where the screen asking doesn't offer My groups (`enabled`).
 */
export function useMyGroups(enabled = true) {
  const session = useSession();
  const { strongerMemoryGroupActorUuid: parent } = useConfig();
  return useQuery({
    queryKey: queryKeys.myGroups(),
    enabled: enabled && session.status === 'authenticated' && parent !== undefined,
    staleTime: 60_000,
    queryFn: (): Promise<StrongerMemoryGroup[]> => loadStrongerMemoryGroups(parent as string),
  });
}

/** One of the person's groups, from `useMyGroups`; undefined while loading or when not theirs. */
export function useMyGroup(groupActorUuid: string | undefined) {
  const groups = useMyGroups();
  return {
    ...groups,
    group: groups.data?.find((group) => group.actorUuid === groupActorUuid),
  };
}

/**
 * A group the person is not in, by name, for its summary profile (`enabled` once `useMyGroups`
 * has said it isn't theirs). Null where Core doesn't show it to them.
 */
export function useVisibleGroup(groupActorUuid: string | undefined, enabled = true) {
  const session = useSession();
  const { strongerMemoryGroupActorUuid: parent } = useConfig();
  return useQuery({
    queryKey: queryKeys.visibleGroup(groupActorUuid ?? 'none'),
    enabled:
      enabled &&
      session.status === 'authenticated' &&
      parent !== undefined &&
      groupActorUuid !== undefined,
    staleTime: 60_000,
    queryFn: () => findVisibleSubGroup(parent as string, groupActorUuid as string),
  });
}

/** "Create group": the new group's actor id once Core has it, and My groups refreshed. */
export function useCreateGroup() {
  const queryClient = useQueryClient();
  const { strongerMemoryGroupActorUuid: parent } = useConfig();
  return useMutation({
    mutationFn: (name: string) => {
      if (!parent) throw new Error('No Stronger Memory group in this environment');
      return createStrongerMemoryGroup(name, parent);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.myGroups() }),
  });
}

/**
 * Everyone in a group the person leads, with the person marked `you`. Only leaders may list a
 * group's members (`enabled`), until Core lets members see each other's names.
 */
export function useGroupPeople(groupActorUuid: string | undefined, enabled = true) {
  const me = useMe();
  const meUuid = me.data?.uuid;
  return useQuery({
    queryKey: queryKeys.groupPeople(groupActorUuid ?? 'none'),
    enabled: enabled && groupActorUuid !== undefined && meUuid !== undefined,
    staleTime: 30_000,
    queryFn: (): Promise<GroupPerson[]> => loadGroupPeople(groupActorUuid as string, meUuid),
  });
}

/** Invitations a group's leaders sent that nobody has answered yet. */
export function useGroupInvites(groupActorUuid: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.groupInvites(groupActorUuid ?? 'none'),
    enabled: enabled && groupActorUuid !== undefined,
    staleTime: 30_000,
    queryFn: () => loadPendingInvites(groupActorUuid as string),
  });
}

function rolesQuery(groupActorUuid: string) {
  return {
    queryKey: queryKeys.groupRoles(groupActorUuid),
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: () => loadGroupRoles(groupActorUuid),
  };
}

/** Access level › Save, and "Accept - Become Facilitator": gives a person a title. */
export function useSetGroupRole(groupActorUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ personId, role }: { personId: string; role: GroupRole }) => {
      const roles = await queryClient.ensureQueryData(rolesQuery(groupActorUuid));
      await setGroupRole(groupActorUuid, personId, role, roles);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.groupPeople(groupActorUuid) }),
  });
}

/**
 * Remove from group, or leaving it (the person themselves). Leaving takes the group out of My
 * groups, so both lists refresh.
 */
export function useRemoveGroupMember(groupActorUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (personId: string) => removeGroupMember(groupActorUuid, personId),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.groupPeople(groupActorUuid) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.myGroups() }),
      ]),
  });
}

/** Edit group name › Save: renamed in Core, and My groups, which every screen reads, refreshes. */
export function useRenameGroup(groupActorUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => renameGroup(groupActorUuid, name),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.myGroups() }),
  });
}

/** Delete group: gone from Core and from My groups. */
export function useDeleteGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (groupActorUuid: string) => deleteGroup(groupActorUuid),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.myGroups() }),
  });
}

/**
 * "Send N invites": each address on Ready to invite gets Core's invitation and leaves the list,
 * then the pending invitations refresh. Resolves to how many were sent; a failure stops there,
 * leaving the ones not sent on the list to try again.
 */
export function useSendInvites(groupActorUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invites: readonly InviteDraft[]) => {
      const roles = await queryClient.ensureQueryData(rolesQuery(groupActorUuid));
      for (const invite of invites) {
        await inviteToGroup(groupActorUuid, invite, roles);
        removeReadyInvite(groupActorUuid, invite.id);
      }
      return invites.length;
    },
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.groupInvites(groupActorUuid) }),
  });
}

/** Pending invites › Resend: Core sends that invitation again; the list stays as it was. */
export function useResendInvite(groupActorUuid: string) {
  return useMutation({
    mutationFn: (invite: Pick<PendingInvite, 'id' | 'inviteeId'>) =>
      resendInvite(groupActorUuid, invite),
  });
}

/** Pending invites › Withdraw invite: Core withdraws it, and it leaves the list. */
export function useWithdrawInvite(groupActorUuid: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteUuid: string) => withdrawInvite(inviteUuid),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.groupInvites(groupActorUuid) }),
  });
}

/** An invitation sent to the signed-in person (Join group), by the id in its link. */
export function useMyInvite(inviteUuid: string | undefined) {
  const session = useSession();
  return useQuery({
    queryKey: queryKeys.myInvite(inviteUuid ?? 'none'),
    enabled: session.status === 'authenticated' && inviteUuid !== undefined,
    queryFn: () => loadMyInvite(inviteUuid as string),
  });
}

/** The invitations waiting for the person's answer, for the Groups tab. */
export function useMyPendingInvites(enabled = true) {
  const session = useSession();
  const { strongerMemoryGroupActorUuid: parent } = useConfig();
  return useQuery({
    queryKey: queryKeys.myPendingInvites(),
    enabled: enabled && session.status === 'authenticated' && parent !== undefined,
    queryFn: () => loadMyPendingInvites(parent as string),
  });
}

/** Join group: the group becomes one of theirs, and the invitation is answered. */
export function useAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteUuid: string) => acceptInvite(inviteUuid),
    onSuccess: (_, inviteUuid) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.myGroups() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.myInvite(inviteUuid) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.myPendingInvites() }),
      ]),
  });
}
